import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { DateTime } from 'luxon';
import { ColDef, CellValueChangedEvent, MenuItemDef, GetContextMenuItems, DefaultMenuItem, GridApi, GetRowIdParams } from 'ag-grid-community';
import { DataGrid } from '../../../../ui/data-grid';
import { SheetTabs } from '../../../../spreadsheet/components/SheetTabs';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useModels } from '../../../../../lib/supabase/models/supabaseModelService';
import './daily-fan-gain-spreadsheet.css';

import {
  useFanGainEntriesForMonth,
  useUpsertFanGainEntry,
  useFanGainAssignments,
  useRealtimeFanGainManager,
  FanGainMarker,
} from '../../../../../lib/supabase/fan-gain/supabaseFanGainService';
import SupabaseFanGainService from '../../../../../lib/supabase/fan-gain/supabaseFanGainService';
import { useTabs } from '../../../../../hooks/useTabs';

interface DailyFanGainSpreadsheetProps {
  defaultMonth: number;
  defaultYear: number;
  isFullscreen?: boolean;
}

type DailyFanGainGroup = 'A' | 'B' | 'C' | 'D' | '1' | '2' | '3' | '2nd Pages' | 'Paused';

type RowType = 'dayTotal' | 'dayAvg' | 'input' | 'middle';

interface GridRowData {
  id: string;
  rowType: RowType;
  model_id?: string;
  modelName?: string;
  A?: string | number;
  B?: string;
  [key: string]: any;
}

export function DailyFanGainSpreadsheet({
  defaultMonth,
  defaultYear,
}: DailyFanGainSpreadsheetProps) {
  const { user } = useAuth();
  const gridRef = useRef<GridApi | null>(null);

  const month = useMemo(() => defaultMonth, [defaultMonth]);
  const year = useMemo(() => defaultYear, [defaultYear]);

  const { activeTab: activeGroup, handleTabChange } = useTabs<string>('A');
  const isInternalSheet = useMemo(() => String(activeGroup).endsWith('-internal'), [activeGroup]);
  const baseGroup = useMemo(() =>
    (isInternalSheet ? String(activeGroup).replace('-internal', '') : activeGroup) as DailyFanGainGroup,
    [activeGroup, isInternalSheet]
  );

  // Stable model order - set once on initial load and never changes until sheet refresh
  const [stableModelOrder, setStableModelOrder] = useState<{ id: string; name: string }[]>([]);

  // Track initial data load to avoid unnecessary transactions during setup
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Track if we've set the initial model order (should only happen once per sheet)
  const [hasSetInitialOrder, setHasSetInitialOrder] = useState(false);

  // Data
  const { data: allModels = [], isLoading: modelsLoading } = useModels();
  const { data: assignments = [] } = useFanGainAssignments();

  // Fan Gain reference entries (GG swaps)
  const { data: ggEntries = [], isLoading: ggLoading } = useFanGainEntriesForMonth(year, month, 'gg-swaps');

  // Daily Fan Gain input entries (stored as fan-gain type)
  const { data: inputEntries = [], isLoading: inputLoading } = useFanGainEntriesForMonth(year, month, 'fan-gain');

  // Fan Gain markers (PROMO_CHANGE and UNSYNCED)
  const [markers, setMarkers] = useState<FanGainMarker[]>([]);

  const upsertEntryMutation = useUpsertFanGainEntry();

  // Real-time updates
  useRealtimeFanGainManager({
    enabled: true,
    ignoreuser_id: user?.id
  });

  // Fetch markers for the current month
  useEffect(() => {
    const fetchMarkers = async () => {
      try {
        const startDate = DateTime.fromObject({ year: year, month: month, day: 1 }, { zone: 'UTC' });
        const endDate = startDate.endOf("month");
        const fetchedMarkers = await SupabaseFanGainService.getMarkersInDateRange({
          startDate,
          endDate,
          type: 'PROMO_CHANGE'
        });
        const unsyncedMarkers = await SupabaseFanGainService.getMarkersInDateRange({
          startDate,
          endDate,
          type: 'UNSYNCED'
        });
        setMarkers([...fetchedMarkers, ...unsyncedMarkers]);
      } catch (error) {
        console.error('Error fetching markers:', error);
      }
    };

    fetchMarkers();
  }, [year, month]);

  // Days in current month
  const daysInMonth = useMemo(() => {
    const dt = DateTime.fromObject({ year, month });
    return dt.daysInMonth || 31;
  }, [year, month]);

  // Assigned model ids for current user (for highlighting)
  const assignedModelIds = useMemo(() => {
    if (!user?.id) return new Set<string>();
    const set = new Set<string>();
    assignments.forEach(a => {
      if (a.user_id === user.id) set.add(a.model_id);
    });
    return set;
  }, [assignments, user?.id]);

  // Build maps for GG daily values and Input values
  const ggMap = useMemo(() => {
    const map = new Map<string, number>();
    ggEntries.forEach(e => {
      const d = DateTime.fromISO(e.date);
      const day = d.day;
      const key = `${e.model_id}_day${day}`;
      map.set(key, e.value || 0);
    });
    return map;
  }, [ggEntries]);

  const inputMap = useMemo(() => {
    const map = new Map<string, number>();
    inputEntries.forEach(e => {
      const d = DateTime.fromISO(e.date);
      const day = d.day;
      const key = `${e.model_id}_day${day}`;
      map.set(key, e.value || 0);
    });
    return map;
  }, [inputEntries]);

  // Build maps for marker lookups
  const promoChangeMarkers = useMemo(() => {
    const map = new Map<string, boolean>();
    markers.filter(m => m.type === 'PROMO_CHANGE').forEach(m => {
      const d = DateTime.fromISO(m.date);
      const day = d.day;
      const key = `${m.model_id}_day${day}`;
      map.set(key, true);
    });
    return map;
  }, [markers]);

  // Helper function to get the start day for calculations based on PROMO_CHANGE markers
  const getPromoChangeStartDay = useCallback((modelId: string): number => {
    const modelMarkers = markers
      .filter(m => m.type === 'PROMO_CHANGE' && m.model_id === modelId)
      .map(m => {
        const d = DateTime.fromISO(m.date);
        return d.day;
      })
      .sort((a, b) => b - a); // Sort descending to get most recent first

    // Return the day of the most recent promo change (inclusive), or 1 if no markers
    return modelMarkers.length > 0 ? modelMarkers[0] : 1;
  }, [markers]);

  // Helper function to check if a specific day has a PROMO_CHANGE marker
  const hasPromoChangeMarker = useCallback((modelId: string, day: number): boolean => {
    return promoChangeMarkers.has(`${modelId}_day${day}`);
  }, [promoChangeMarkers]);

  const unsyncedMarkers = useMemo(() => {
    const map = new Map<string, boolean>();
    markers.filter(m => m.type === 'UNSYNCED').forEach(m => {
      const d = DateTime.fromISO(m.date);
      const day = d.day;
      const key = `${m.model_id}_day${day}`;
      map.set(key, true);
    });
    return map;
  }, [markers]);

  // Reset initial order flag when sheet context changes
  useEffect(() => {
    setHasSetInitialOrder(false);
  }, [baseGroup, year, month, isInternalSheet]);

  // Set initial model order only once when ALL data is fully loaded
  useEffect(() => {
    // Skip if we've already set the initial order
    if (hasSetInitialOrder) return;

    // Wait for all data to finish loading
    if (modelsLoading || ggLoading || inputLoading) return;

    // Wait for models to be available
    if (allModels.length === 0) return;

    const filtered = allModels
      .filter(model =>
        model.fan_gain_group === baseGroup &&
        (model.account_type === 'Normal' || model.account_type === 'Unlock')
      );

    // Calculate averages for sorting
    const modelsWithAvg = filtered.map(model => {
      const startDay = getPromoChangeStartDay(model.id);
      const values: number[] = [];

      for (let d = startDay; d <= daysInMonth; d++) {
        const inputVal = inputMap.get(`${model.id}_day${d}`) || 0;
        const ggToday = ggMap.get(`${model.id}_day${d}`) || 0;
        const ggNext = d === 31 ? 0 : (ggMap.get(`${model.id}_day${d + 1}`) || 0);
        const diffVal = ggNext - ggToday;
        const finalVal = Math.max(0, isInternalSheet ? (inputVal - diffVal) : inputVal);

        // For internal sheets, don't show value if next day is empty (prevents inaccurate large values)
        if (isInternalSheet && d < daysInMonth && ggNext === 0) {
          continue;
        }

        if (finalVal > 0) values.push(finalVal);
      }

      const avg = values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;
      return { model, avg };
    });

    // Sort by average (highest first), then by name as fallback
    const sortedModels = modelsWithAvg
      .sort((a, b) => b.avg - a.avg || a.model.name.localeCompare(b.model.name))
      .map(item => ({ id: item.model.id, name: item.model.name }));

    setStableModelOrder(sortedModels);
    setHasSetInitialOrder(true);
  }, [hasSetInitialOrder, modelsLoading, ggLoading, inputLoading, allModels, baseGroup, daysInMonth, inputMap, ggMap, isInternalSheet, getPromoChangeStartDay]);

  // Use stable model order for row building
  const groupModels = stableModelOrder;

  // Build initial row data only once - subsequent updates use transactions
  const initialRowData: GridRowData[] = useMemo(() => {
    if (!groupModels.length) return [];

    const rows: GridRowData[] = [];
    const groupLabel = (baseGroup === '2nd Pages' || baseGroup === 'Paused')
      ? baseGroup.toUpperCase()
      : `GROUP ${baseGroup}`;

    // Calculate data for all models
    const entriesByModel: { [model_id: string]: { [day: string]: { input: number; diff: number; middle: number } } } = {};

    groupModels.forEach(model => {
      entriesByModel[model.id] = {};

      for (let day = 1; day <= 31; day++) {
        const dayKey = `day${day}`;
        const inputVal = inputMap.get(`${model.id}_${dayKey}`) || 0;
        const ggToday = ggMap.get(`${model.id}_${dayKey}`) || 0;
        const ggNext = day === 31 ? 0 : (ggMap.get(`${model.id}_day${day + 1}`) || 0);
        const diffVal = ggNext - ggToday;
        const middleVal = Math.max(0, inputVal - diffVal);

        entriesByModel[model.id][dayKey] = {
          input: inputVal,
          diff: diffVal,
          middle: middleVal
        };
      }
    });

    // Calculate totals and averages
    const dayTotals: Record<string, number> = {};
    const dayAverages: Record<string, number> = {};

    for (let day = 1; day <= daysInMonth; day++) {
      const dayKey = `day${day}`;
      const values: number[] = [];
      const nonZero: number[] = [];

      groupModels.forEach(model => {
        const modelData = entriesByModel[model.id];
        if (!modelData || !modelData[dayKey]) return;

        const value = Math.max(0, isInternalSheet
          ? modelData[dayKey].middle
          : modelData[dayKey].input
        );

        // For internal sheets, don't include value if next day is empty
        if (isInternalSheet && day < daysInMonth) {
          const nextDayKey = `day${day + 1}`;
          const ggNext = ggMap.get(`${model.id}_${nextDayKey}`) || 0;
          if (ggNext === 0) {
            values.push(0);
            return;
          }
        }

        values.push(value);
        if (value > 0) nonZero.push(value);
      });

      dayTotals[dayKey] = values.reduce((s, v) => s + v, 0);
      dayAverages[dayKey] = nonZero.length > 0
        ? Math.round(nonZero.reduce((s, v) => s + v, 0) / nonZero.length)
        : 0;
    }

    // Add total row first
    const totalRow: GridRowData = {
      id: 'day-total',
      rowType: 'dayTotal',
      A: groupLabel,
      B: 'DAY TOTAL'
    };

    for (let day = 1; day <= 31; day++) {
      const dayKey = `day${day}`;
      if (day <= daysInMonth) {
        totalRow[dayKey] = dayTotals[dayKey] || 0;
      } else {
        totalRow[dayKey] = '';
      }
    }
    rows.push(totalRow);

    // Add average row
    const avgRow: GridRowData = {
      id: 'day-avg',
      rowType: 'dayAvg',
      A: groupLabel,
      B: 'DAY AVG'
    };

    for (let day = 1; day <= 31; day++) {
      const dayKey = `day${day}`;
      if (day <= daysInMonth) {
        avgRow[dayKey] = dayAverages[dayKey] || 0;
      } else {
        avgRow[dayKey] = '';
      }
    }
    rows.push(avgRow);

    // Add model rows
    groupModels.forEach(model => {
      const modelData = entriesByModel[model.id];

      // Calculate average (from promo change date if set)
      const startDay = getPromoChangeStartDay(model.id);
      const nonZero: number[] = [];

      for (let d = startDay; d <= daysInMonth; d++) {
        const dayKey = `day${d}`;
        const data = modelData?.[dayKey];
        if (!data) continue;

        const v = Math.max(0, isInternalSheet ? data.middle : data.input);

        // For internal sheets, don't include value if next day is empty
        if (isInternalSheet && d < daysInMonth) {
          const nextDayKey = `day${d + 1}`;
          const ggNext = ggMap.get(`${model.id}_${nextDayKey}`) || 0;
          if (ggNext === 0) {
            continue;
          }
        }

        if (v > 0) nonZero.push(v);
      }

      const avg = nonZero.length > 0
        ? Math.round(nonZero.reduce((s, v) => s + v, 0) / nonZero.length)
        : 0;

      const row: GridRowData = {
        id: model.id,
        rowType: isInternalSheet ? 'middle' : 'input',
        model_id: model.id,
        modelName: model.name,
        A: avg === 0 ? '' : String(avg),
        B: model.name
      };

      for (let day = 1; day <= 31; day++) {
        const dayKey = `day${day}`;
        if (day <= daysInMonth) {
          const data = modelData?.[dayKey];
          let value = data ? Math.max(0, Math.round(isInternalSheet ? data.middle : data.input)) : 0;

          // For internal sheets, don't show value if next day is empty
          if (isInternalSheet && day < daysInMonth) {
            const nextDayKey = `day${day + 1}`;
            const ggNext = ggMap.get(`${model.id}_${nextDayKey}`) || 0;
            if (ggNext === 0) {
              value = 0;
            }
          }

          row[dayKey] = value;
        } else {
          row[dayKey] = '';
        }
      }

      rows.push(row);
    });

    return rows;
  }, [groupModels, daysInMonth, baseGroup, isInternalSheet, getPromoChangeStartDay, inputMap, ggMap]);

  // Handle data updates via transactions instead of full row data refresh
  useEffect(() => {
    if (!gridRef.current || isInitialLoad || !groupModels.length) return;

    const updatedRows: GridRowData[] = [];

    // Recalculate affected rows
    groupModels.forEach(model => {
      const modelData: { [day: string]: { input: number; diff: number; middle: number } } = {};

      for (let day = 1; day <= 31; day++) {
        const dayKey = `day${day}`;
        const inputVal = inputMap.get(`${model.id}_${dayKey}`) || 0;
        const ggToday = ggMap.get(`${model.id}_${dayKey}`) || 0;
        const ggNext = day === 31 ? 0 : (ggMap.get(`${model.id}_day${day + 1}`) || 0);
        const diffVal = ggNext - ggToday;
        const middleVal = Math.max(0, inputVal - diffVal);

        modelData[dayKey] = {
          input: inputVal,
          diff: diffVal,
          middle: middleVal
        };
      }

      // Calculate average
      const startDay = getPromoChangeStartDay(model.id);
      const nonZero: number[] = [];

      for (let d = startDay; d <= daysInMonth; d++) {
        const dayKey = `day${d}`;
        const data = modelData[dayKey];
        if (!data) continue;

        const v = Math.max(0, isInternalSheet ? data.middle : data.input);

        // For internal sheets, don't include value if next day is empty
        if (isInternalSheet && d < daysInMonth) {
          const nextDayKey = `day${d + 1}`;
          const ggNext = ggMap.get(`${model.id}_${nextDayKey}`) || 0;
          if (ggNext === 0) {
            continue;
          }
        }

        if (v > 0) nonZero.push(v);
      }

      const avg = nonZero.length > 0
        ? Math.round(nonZero.reduce((s, v) => s + v, 0) / nonZero.length)
        : 0;

      const row: GridRowData = {
        id: model.id,
        rowType: isInternalSheet ? 'middle' : 'input',
        model_id: model.id,
        modelName: model.name,
        A: avg === 0 ? '' : String(avg),
        B: model.name
      };

      for (let day = 1; day <= 31; day++) {
        const dayKey = `day${day}`;
        if (day <= daysInMonth) {
          const data = modelData[dayKey];
          let value = data ? Math.max(0, Math.round(isInternalSheet ? data.middle : data.input)) : 0;

          // For internal sheets, don't show value if next day is empty
          if (isInternalSheet && day < daysInMonth) {
            const nextDayKey = `day${day + 1}`;
            const ggNext = ggMap.get(`${model.id}_${nextDayKey}`) || 0;
            if (ggNext === 0) {
              value = 0;
            }
          }

          row[dayKey] = value;
        } else {
          row[dayKey] = '';
        }
      }

      updatedRows.push(row);
    });

    // Also update totals and averages
    const dayTotals: Record<string, number> = {};
    const dayAverages: Record<string, number> = {};

    for (let day = 1; day <= daysInMonth; day++) {
      const dayKey = `day${day}`;
      const values: number[] = [];
      const nonZero: number[] = [];

      updatedRows.forEach(row => {
        const value = typeof row[dayKey] === 'number' ? row[dayKey] : 0;
        values.push(value);
        if (value > 0) nonZero.push(value);
      });

      dayTotals[dayKey] = values.reduce((s, v) => s + v, 0);
      dayAverages[dayKey] = nonZero.length > 0
        ? Math.round(nonZero.reduce((s, v) => s + v, 0) / nonZero.length)
        : 0;
    }

    const groupLabel = (baseGroup === '2nd Pages' || baseGroup === 'Paused')
      ? baseGroup.toUpperCase()
      : `GROUP ${baseGroup}`;

    const totalRow: GridRowData = {
      id: 'day-total',
      rowType: 'dayTotal',
      A: groupLabel,
      B: 'DAY TOTAL'
    };

    const avgRow: GridRowData = {
      id: 'day-avg',
      rowType: 'dayAvg',
      A: groupLabel,
      B: 'DAY AVG'
    };

    for (let day = 1; day <= 31; day++) {
      const dayKey = `day${day}`;
      if (day <= daysInMonth) {
        totalRow[dayKey] = dayTotals[dayKey] || 0;
        avgRow[dayKey] = dayAverages[dayKey] || 0;
      } else {
        totalRow[dayKey] = '';
        avgRow[dayKey] = '';
      }
    }

    // Use transaction to update all rows efficiently
    gridRef.current.applyTransaction({
      update: [totalRow, avgRow, ...updatedRows]
    });

  }, [inputMap, ggMap, isInternalSheet, getPromoChangeStartDay, daysInMonth, baseGroup, groupModels, isInitialLoad]);

  // Mark initial load as complete after first render
  useEffect(() => {
    if (initialRowData.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [initialRowData.length, isInitialLoad]);

  // Handle cell value changes with optimistic updates
  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent) => {
    const { data, colDef, newValue, oldValue } = event;
    if (!user?.id) return;
    if (data?.rowType !== 'input') return;

    const field = colDef.field;
    if (!field || !field.startsWith('day')) return;

    const dayNumber = parseInt(field.replace('day', ''));
    if (isNaN(dayNumber) || dayNumber < 1 || dayNumber > daysInMonth) return;

    const model_id = data.model_id as string;
    const value = parseFloat(newValue) || 0;
    const prev = parseFloat(oldValue) || 0;
    if (value === prev) return;

    try {
      const date = DateTime.fromObject({ year: year, month: month, day: dayNumber }, { zone: 'UTC' });
      await upsertEntryMutation.mutateAsync({
        model_id,
        date,
        type: 'fan-gain',
        value
      });
    } catch (e) {
      // Revert on failure
      event.node.setDataValue(field, oldValue);
    }
  }, [user?.id, daysInMonth, year, month, upsertEntryMutation]);

  // Marker management functions
  const handleToggleMarker = useCallback(async (modelId: string, day: number, markerType: 'PROMO_CHANGE' | 'UNSYNCED') => {
    if (!user?.id) return;

    try {
      const date = DateTime.fromObject({ year: year, month: month, day: day }, { zone: 'UTC' });
      const wasAdded = await SupabaseFanGainService.toggleMarker(modelId, date, markerType);

      if (wasAdded) {
        const newMarker: FanGainMarker = {
          id: `temp-${DateTime.now()}`,
          model_id: modelId,
          date: date.toISO() || '',
          type: markerType,
          created_at: DateTime.utc().toISO()
        };
        setMarkers(prev => [...prev, newMarker]);
      } else {
        setMarkers(prev => prev.filter(m =>
          !(m.model_id === modelId && DateTime.fromISO(m.date).day === day && m.type === markerType)
        ));
      }
    } catch (error) {
      console.error(`Error toggling ${markerType} marker:`, error);
    }
  }, [user?.id, year, month]);

  // Context menu: mark promo change and markers
  const handleGetContextMenuItems: GetContextMenuItems = useCallback((params) => {
    const items: (DefaultMenuItem | MenuItemDef)[] = [];
    const row = params.node?.data as GridRowData | undefined;
    const colField = params.column?.getColId();

    if (row && row.rowType === 'middle' && row.model_id && colField && colField.startsWith('day')) {
      const day = parseInt(colField.replace('day', ''));
      if (!isNaN(day) && day >= 1 && day <= daysInMonth) {
        items.push({
          name: 'Markers',
          subMenu: [
            {
              name: 'Toggle Promo Changed Marker',
              action: () => {
                handleToggleMarker(row.model_id!, day, 'PROMO_CHANGE');
              }
            },
            {
              name: 'Toggle Unsynced Marker',
              action: () => {
                handleToggleMarker(row.model_id!, day, 'UNSYNCED');
              }
            }
          ]
        });
      }
    }

    return items.length > 0 ? items : (params.defaultItems ?? []);
  }, [daysInMonth, markers, handleToggleMarker]);

  // Essential for efficient updates - AG Grid uses this to identify rows
  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.id;
  }, []);

  // Helper function to find the most recent day with data for a model
  const getMostRecentDayWithData = useCallback((modelId: string): number | null => {
    for (let day = daysInMonth; day >= 1; day--) {
      const inputVal = inputMap.get(`${modelId}_day${day}`) || 0;
      if (inputVal > 0) {
        return day;
      }
    }
    return null;
  }, [daysInMonth, inputMap]);

  // Helper function to calculate 5-day average (current + previous 4 days)
  const getFiveDayAverage = useCallback((modelId: string, currentDay: number): number => {
    const values: number[] = [];
    const startDay = Math.max(1, currentDay - 4);

    for (let day = startDay; day <= currentDay; day++) {
      const inputVal = inputMap.get(`${modelId}_day${day}`) || 0;
      values.push(inputVal);
    }

    if (values.length === 0) return 0;
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }, [inputMap]);

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        field: 'A',
        headerName: '',
        width: 110,
        resizable: false,
        pinned: 'left',
        editable: false,
        valueFormatter: (p) => p.value || '',
        cellClass: 'col-a',
        cellClassRules: {
          'total-cell': (p) => p.data?.rowType === 'dayTotal',
          'avg-cell': (p) => p.data?.rowType === 'dayAvg',
          'zero-cell': (p) => p.value === '' || p.value === '0',
        }
      },
      {
        field: 'B',
        headerName: '',
        width: 140,
        resizable: false,
        pinned: 'left',
        editable: false,
        cellClass: 'col-b',
        cellClassRules: {
          'total-cell': (p) => p.data?.rowType === 'dayTotal',
          'avg-cell': (p) => p.data?.rowType === 'dayAvg',
          'zero-cell': (p) => p.value === '' || p.value === '0',
        }
      }
    ];

    for (let day = 1; day <= 31; day++) {
      const field = `day${day}`;
      const inMonth = day <= daysInMonth;
      cols.push({
        field,
        headerName: inMonth ? String(day) : '',
        width: 100,
        type: 'numericColumn',
        editable: (params) => inMonth && params.data?.rowType === 'input',
        cellEditor: 'agTextCellEditor',
        valueParser: (params) => {
          const v = params.newValue;
          return v === '' ? 0 : Math.max(0, Math.round(parseFloat(v) || 0));
        },
        valueFormatter: (params) => {
          const value = params.value;
          if (value === 0 || value === '' || value === null || value === undefined) return '';
          return String(value);
        },
        headerClass: 'day-header',
        cellClass: 'day-cell',
        cellClassRules: {
          'total-cell': (p) => p.data?.rowType === 'dayTotal',
          'avg-cell': (p) => p.data?.rowType === 'dayAvg',
          'zero-cell': (p) => p.value === 0 || p.value === '',
          'assigned-cell': (p) => !!p.data?.model_id && assignedModelIds.has(p.data.model_id),
          'promo-change-cell': (p) => !!p.data?.model_id && hasPromoChangeMarker(p.data.model_id, day),
          'promo-change-marker': (p) => !!p.data?.model_id && promoChangeMarkers.has(`${p.data.model_id}_day${day}`),
          'unsynced-marker': (p) => !!p.data?.model_id && unsyncedMarkers.has(`${p.data.model_id}_day${day}`),
          'low-average-warning': (p) => {
            if (isInternalSheet || p.data?.rowType !== 'input' || !p.data?.model_id) return false;

            const mostRecentDay = getMostRecentDayWithData(p.data.model_id);
            if (mostRecentDay !== day) return false;

            const fiveDayAverage = getFiveDayAverage(p.data.model_id, day);
            return fiveDayAverage < 1000;
          },
        }
      });
    }

    return cols;
  }, [daysInMonth, baseGroup, assignedModelIds, hasPromoChangeMarker, isInternalSheet, getMostRecentDayWithData, getFiveDayAverage, promoChangeMarkers, unsyncedMarkers]);

  // Tabs
  const sheetTabs = useMemo(() => {
    const tabs: { id: string; label: string; bottomBorderColor?: string }[] = [];
    const groups: DailyFanGainGroup[] = ['A', 'B', 'C', 'D', '1', '2', '3'];
    groups.forEach(group => {
      tabs.push({
        id: group,
        label: `Group ${group}`,
        bottomBorderColor: groupColors[group]
      });
      tabs.push({
        id: `${group}-internal`,
        label: `Group ${group} Internal`,
        bottomBorderColor: groupColors[group]
      });
    });
    tabs.push({
      id: '2nd Pages',
      label: '2nd Pages',
      bottomBorderColor: groupColors['2nd Pages']
    });
    tabs.push({
      id: 'Paused',
      label: 'Paused',
      bottomBorderColor: groupColors['Paused']
    });
    return tabs;
  }, []);

  // Group class to theme certain cells via CSS (improves perf vs inline styles)
  const groupCssClass = useMemo(() => {
    const raw = String(baseGroup).toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const safe = raw.match(/^[0-9]/) ? `n-${raw}` : raw;
    return `fan-gain-grid ${safe}`;
  }, [baseGroup]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col">
        <DataGrid
          columnDefs={columnDefs}
          rowData={initialRowData}
          height={'calc(100vh - 180px)'}
          className={`min-h-[400px] rounded-b-none ${groupCssClass}`}
          defaultColDef={{
            sortable: false,
            filter: false,
            resizable: false,
            suppressHeaderFilterButton: true,
            suppressHeaderMenuButton: true,
            minWidth: 100,
          }}
          gridOptions={{
            domLayout: 'normal',
            headerHeight: 42,
            rowHeight: 26,
            rowBuffer: 3,
            debounceVerticalScrollbar: true,
            suppressRowHoverHighlight: true,
            getContextMenuItems: handleGetContextMenuItems,
            onCellValueChanged: handleCellValueChanged,
            getRowId: getRowId,
            onGridReady: (params) => {
              gridRef.current = params.api;
            },
            // Performance optimizations to reduce flashing
            animateRows: false,
            suppressScrollOnNewData: true,
            suppressColumnVirtualisation: false,
            suppressRowVirtualisation: false,
            // Improve transaction performance
            deltaSort: true,
            // Suppress animations during data updates
            suppressAnimationFrame: true,
          }}
        />
        <SheetTabs
          tabs={sheetTabs}
          activeTab={String(activeGroup)}
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );
}

export default DailyFanGainSpreadsheet;

// Colors used for headers and group styling
const groupColors: { [key in DailyFanGainGroup]: string } = {
  'A': '#FF0000',
  '2nd Pages': '#FF0000',
  'B': '#6AA84F',
  'C': '#999999',
  'D': '#674EA7',
  '1': '#E69138',
  '2': '#6D9EEB',
  '3': '#C27BA0',
  'Paused': '#660000'
};